<template>
    <div class="kv-textarea" :class="[sizeClass, `kv-${variant}-input`, { 'kv-disabled': disabled, 'kv-error': showError, 'kv-readonly': readonly }]" @click="$emit('click')">
        <label v-if="label" class="kv-label" :for="id">{{ label }}</label>

        <div class="kv-input-wrapper">
            <slot name="prepend"></slot>

            <div class="kv-input-field">
                <textarea 
                    ref="kvInputRef" 
                    :id="id" 
                    :rows="rows"
                    :disabled="disabled" 
                    :readonly="readonly"
                    :maxlength="maxlength"
                    v-model="internalValue" 
                    @input="onInput" 
                    @change="$emit('change', internalValue)" 
                    @focus="$emit('focus', $event)" 
                    @blur="onBlur" 
                    :aria-invalid="showError ? 'true' : 'false'" 
                    class="kv-native-input"
                    :style="{ resize: resize ? 'vertical' : 'none' }"
                ></textarea>
                
                <span v-show="showPlaceholder" class="kv-placeholder">{{ placeholder }}</span>
            </div>
            
            <slot name="append"></slot>
        </div>

        </div>
</template>

<script>
export default {
    name: 'KvTextarea',
    model: {
        prop: 'value',
        event: 'input'
    },
    props: {
        value: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            default: ''
        },
        rows: {
            type: [String, Number],
            default: 3
        },
        resize: {
            type: Boolean,
            default: false
        },
        variant: {
            type: String,
            default: 'tonal' // tonal, outlined
        },
        size: {
            type: String,
            default: 'regular'
        },
        placeholder: {
            type: String,
            default: ''
        },
        alwaysValidate: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        hint: {
            type: String,
            default: ''
        },
        id: {
            type: String,
            default() {
                return 'kv-textarea-' + Math.random().toString(36).substr(2, 9)
            }
        },
        validator: {
            type: Function,
            default: null
        },
        hasError: {
            type: Boolean,
            default: false
        },
        maxlength: {
            type: [String, Number],
            default: null
        }
    },
    data() {
        return {
            internalValue: this.value,
            touched: false || this.alwaysValidate,
            validationResult: { valid: true, message: '' }
        }
    },
    computed: {
        sizeClass() {
            return `kv-${this.size}-input`;
        },
        showPlaceholder() {
            return this.placeholder && (this.internalValue === null || this.internalValue === undefined || this.internalValue === '');
        },
        showError() {
            return this.hasError || (!this.validationResult.valid && this.touched);
        },
        errorMessage() {
            return this.validationResult.message || ''
        }
    },
    watch: {
        value(val) {
            this.internalValue = val
            this.validate()
        }
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        focus() {
            if (this.$refs.kvInputRef) {
                this.$refs.kvInputRef?.focus()
            }
        },
        onInput(event) {
            this.$emit('input', this.internalValue)
            this.validate()
        },
        onBlur(event) {
            this.touched = true
            this.validate()
            this.$emit('blur', event)
        },
        validate() {
            if (typeof this.validator === 'function') {
                try {
                    const result = this.validator(this.internalValue)
                    if (typeof result === 'boolean') {
                        this.validationResult = { valid: result, message: result ? '' : this.t('inputs-validation-invalid_value') }
                    } else if (result && typeof result === 'object') {
                        this.validationResult = {
                            valid: result.valid,
                            message: result.message || ''
                        }
                    } else {
                        this.validationResult = { valid: true, message: '' }
                    }
                } catch (err) {
                    this.validationResult = { valid: false, message: err.message || this.t('inputs-validation-validation_error') }
                }
            } else {
                this.validationResult = { valid: true, message: '' }
            }
            return this.validationResult.valid
        }
    },
    mounted() {
        this.validate()
    }
}
</script>

<style lang="less">
.kv-textarea {
    display: flex;
    flex-direction: column;
    font-family: inherit;
    font-size: 0.9rem;
    height: auto; // Textarea cresce, não tem altura fixa como o input

    &.kv-readonly .kv-native-input {
        cursor: inherit;
    }

    // Variantes de Cores
    &.kv-tinted-input .kv-input-wrapper {
        border-color: #2AAA4C;
        background-color: #F3FAF4;
        .kv-native-input { color: #1D7836; }
    }

    &.kv-outlined-input .kv-input-wrapper {
        border-color: #E5E7EB;
        background-color: #FFFFFF;
        .kv-native-input { color: #1F2A37; }
    }

    &.kv-small-input { font-size: 13px; }
    &.kv-large-input {
        font-size: 14px;

        .kv-input-wrapper {
            padding: 12px;
        }

        .kv-native-input {
            min-height: 80px;
        }
    }

    .kv-label {
        margin-bottom: 6px;
        font-size: inherit;
    }

    .kv-input-wrapper {
        display: flex;
        align-items: flex-start; // Diferença importante: alinha itens ao topo
        position: relative;
        background-color: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        color: #1f2a37;
        padding: 10px; // Padding geral
        gap: 8px;
        width: 100%;
    }

    .kv-native-input {
        flex: 1 1 auto;
        border: none;
        outline: none;
        background: transparent;
        padding: 0;
        line-height: 1.5; // Melhor legibilidade para texto longo
        width: 100%;
        min-height: 24px;
        font-family: inherit;
        font-size: inherit;
    }

    .kv-hint {
        margin-top: 6px;
        font-size: 0.85rem;
        color: #6b7280;
    }

    .kv-error-message {
        margin-top: 6px;
        font-size: 0.85rem;
        color: #b91c1c;
    }

    .kv-error .kv-input-wrapper {
        border-color: #f87171;
    }

    .kv-disabled {
        opacity: 0.6;
        pointer-events: none;
    }

    .kv-input-field {
        display: flex;
        position: relative;
        font-size: inherit;
        width: 100%;
        overflow: hidden;
    }

    // Placeholder Logic
    .kv-placeholder {
        position: absolute;
        color: #9ca3af;
        white-space: nowrap;
        opacity: 0;
        left: 0;
        pointer-events: none;
        top: 0; // No textarea fica no topo, não no meio
        font-size: inherit;
        line-height: 1.5; // Mesma line-height do textarea
    }

    .kv-placeholder {
        opacity: 1;
    }
}
</style>